// logger.ts
import chalk from 'chalk';

type LogLevel = 'error' | 'warn' | 'info' | 'http' | 'debug' | 'verbose';
type LogEnvironment = 'dev' | 'staging' | 'prod';

interface LoggerOptions {
  service?: string;
  defaultMeta?: Record<string, any>;
}

class Logger {
  private service: string;
  private defaultMeta: Record<string, any>;
  private environment: LogEnvironment;

  constructor(options: LoggerOptions = {}) {
    this.service = options.service || 'next-app';
    this.defaultMeta = options.defaultMeta || {};
    this.environment = (process.env.ENVIRONMENT as LogEnvironment) || 'dev';

    // Initial log to show logger is initialized
    this.debug(`Logger initialized in ${this.environment} mode`);
  }

  // Helper to determine if we should log this level based on environment
  private shouldLog(level: LogLevel): boolean {
    const levels: Record<LogEnvironment, LogLevel[]> = {
      prod: ['error', 'warn', 'info'],
      staging: ['error', 'warn', 'info', 'http'],
      dev: ['error', 'warn', 'info', 'http', 'debug', 'verbose'],
    };

    return levels[this.environment]?.includes(level) || false;
  }

  // Helper to format timestamp
  private getTimestamp(): string {
    const now = new Date();
    return now.toISOString().replace('T', ' ').substring(0, 19);
  }

  // Helper to format metadata
  private formatMeta(meta?: Record<string, any>): string {
    if (!meta || Object.keys(meta).length === 0) return '';

    try {
      return `\n${JSON.stringify(meta, null, 2)}`;
    } catch (e) {
      return '\n[Unserializable data]';
    }
  }

  // Print log with consistent formatting
  private printLog(level: LogLevel, message: string, meta?: any): void {
    if (!this.shouldLog(level)) return;

    const timestamp = chalk.gray(`[${this.getTimestamp()}]`);
    const service = chalk.cyan(`[${this.service}]`);

    // Colorize level based on severity
    const colorizedLevel = (() => {
      switch (level) {
        case 'error':
          return chalk.bold.red(`[${level.toUpperCase()}]`);
        case 'warn':
          return chalk.bold.yellow(`[${level.toUpperCase()}]`);
        case 'info':
          return chalk.bold.blue(`[${level.toUpperCase()}]`);
        case 'http':
          return chalk.bold.green(`[${level.toUpperCase()}]`);
        case 'debug':
          return chalk.bold.magenta(`[${level.toUpperCase()}]`);
        case 'verbose':
          return chalk.bold.white(`[${level.toUpperCase()}]`);
      }
    })();

    // Base log line
    let output = `${timestamp} ${service} ${colorizedLevel} ${chalk.white(message)}`;

    // Handle metadata
    const combinedMeta = { ...this.defaultMeta, ...(meta || {}) };

    if (meta instanceof Error) {
      // Handle Error objects
      output += `\n${chalk.red(meta.stack || meta.message)}`;
    } else if (Object.keys(combinedMeta).length > 0) {
      // If there's data to show, format it
      const cleanData = { ...combinedMeta };

      // Handle special case for error property within metadata
      if (cleanData.error instanceof Error) {
        output += `\n${chalk.red(cleanData.error.stack || cleanData.error.message)}`;
        delete cleanData.error;
      }

      // Add remaining metadata if there's anything left
      if (Object.keys(cleanData).length > 0) {
        output += this.formatMeta(cleanData);
      }
    }

    // Use appropriate console method
    switch (level) {
      case 'error':
        console.error(output);
        break;
      case 'warn':
        console.warn(output);
        break;
      default:
        console.log(output);
    }
  }

  // Standard logging methods
  error(message: string, meta?: any): void {
    this.printLog('error', message, meta);
  }

  warn(message: string, meta?: any): void {
    this.printLog('warn', message, meta);
  }

  info(message: string, meta?: any): void {
    this.printLog('info', message, meta);
  }

  http(message: string, meta?: any): void {
    this.printLog('http', message, meta);
  }

  debug(message: string, meta?: any): void {
    this.printLog('debug', message, meta);
  }

  verbose(message: string, meta?: any): void {
    this.printLog('verbose', message, meta);
  }

  // Special method for tabular data
  table(message: string, data: any[]): void {
    if (!this.shouldLog('info')) return;

    if (Array.isArray(data) && data.length > 0) {
      const timestamp = chalk.gray(`[${this.getTimestamp()}]`);
      const service = chalk.cyan(`[${this.service}]`);
      const level = chalk.bold.blue(`[INFO]`);

      console.log(`${timestamp} ${service} ${level} ${chalk.white(message)}`);
      console.table(data);
    } else {
      this.warn(`Attempted to log table with invalid data: ${message}`);
    }
  }

  // Smart logging that determines best format
  log(message: string, data?: any): void {
    if (!data) {
      this.info(message);
      return;
    }

    // If it's an error, use error level
    if (data instanceof Error) {
      this.error(message, data);
      return;
    }

    // If it's an array of objects, use table
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      this.table(message, data);
      return;
    }

    // Default to info with the data
    this.info(message, data);
  }
}

// Create singleton instance
const loggerInstance = new Logger();

// Export default instance and class for extensibility
export { Logger };
export default loggerInstance;
